import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { generateId } from '@ibe/utils';

import { type CatalogueRate } from '../../catalogue/api/catalogue-api.js';

export interface SelectedRoom {
  readonly roomTypeId: string;
  readonly ratePlanId: string;
  readonly rooms: number;
  readonly label: string;
  readonly pricePerNight: number;
  readonly quote?: CatalogueRate | undefined;
}

export interface GuestDetails {
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  readonly phone: string;
  readonly addressLine1: string;
  readonly city: string;
  readonly country: string;
  readonly postalCode: string;
  readonly specialRequests: string;
}

export interface BookingState {
  propertyId: string | null;
  propertyName: string;
  checkIn: string;
  checkOut: string;
  adults: number;
  children: number;
  rooms: number;
  selectedRooms: readonly SelectedRoom[];
  guestDetails: GuestDetails | null;
  bookingReference: string | null;
  /** Guest access token after register/login. */
  accessToken: string | null;
  /** Guest ID from the auth response. */
  guestId: string | null;
  /** Reservation ID after pencil booking creation. */
  reservationId: string | null;
  /** Payment ID after payment initiation. */
  paymentId: string | null;
  /** Gateway type: 1 = Quickstream/TrustedFrame, 2 = Windcave. */
  gatewayType: number | null;
  /** Gateway name: 'windcave' or 'quickstream'. */
  gatewayName: string | null;
  /** Grand total in minor units, for payment confirmation. */
  grandTotal: string | null;
  /** Currency of the quoted total. */
  currency: string;
  /** Unique per booking attempt; preserved for retries, regenerated for new attempts. */
  idempotencyKey: string | null;
}

const DEFAULT_STATE: BookingState = {
  propertyId: null,
  propertyName: '',
  checkIn: '',
  checkOut: '',
  adults: 2,
  children: 0,
  rooms: 1,
  selectedRooms: [],
  guestDetails: null,
  bookingReference: null,
  accessToken: null,
  guestId: null,
  reservationId: null,
  paymentId: null,
  gatewayType: null,
  gatewayName: null,
  grandTotal: null,
  currency: 'AUD',
  idempotencyKey: null,
};

const BOOKING_STORAGE_KEY = 'ibe.booking';

function loadBookingState(): BookingState {
  try {
    const raw = sessionStorage.getItem(BOOKING_STORAGE_KEY);
    if (raw === null) return DEFAULT_STATE;
    const parsed = JSON.parse(raw) as Partial<BookingState>;
    return { ...DEFAULT_STATE, ...parsed };
  } catch {
    return DEFAULT_STATE;
  }
}

function saveBookingState(state: BookingState): void {
  try {
    sessionStorage.setItem(BOOKING_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage can be unavailable or full; the booking just won't survive a refresh.
  }
}

export interface BookingContextValue {
  state: BookingState;
  setProperty: (id: string, name: string) => void;
  setDates: (checkIn: string, checkOut: string) => void;
  setOccupancy: (adults: number, children: number, rooms: number) => void;
  setSelectedRooms: (rooms: readonly SelectedRoom[]) => void;
  setGuestDetails: (details: GuestDetails) => void;
  setBookingReference: (ref: string) => void;
  setGuestAuth: (token: string, guestId: string) => void;
  setReservation: (id: string, bookingReference: string, guestId: string) => void;
  setPayment: (paymentId: string) => void;
  setGateway: (type: number, name: string) => void;
  setGrandTotal: (total: string) => void;
  setCurrency: (currency: string) => void;
  setIdempotencyKey: (key: string) => void;
  reset: () => void;
}

const BookingContext = createContext<BookingContextValue | null>(null);

/**
 * The booking a guest is part-way through.
 *
 * **Every setter is stable and the value is memoised**, which is load-bearing
 * rather than an optimisation. Rebuilt on each render, a setter is a new
 * function identity every time — so any consumer that lists one in an effect's
 * dependencies runs that effect on every render, and if the effect sets state
 * the two spin forever. That is not hypothetical: it hung this application's
 * entire test suite, with no failure message, until the setters were made
 * stable.
 *
 * The memoised value also stops every page in the flow re-rendering whenever
 * any part of the booking changes.
 */
export function BookingProvider({ children }: { readonly children: ReactNode }): React.JSX.Element {
  const [state, setState] = useState<BookingState>(loadBookingState);

  useEffect(() => {
    saveBookingState(state);
  }, [state]);

  const setProperty = useCallback((id: string, name: string) => {
    setState((current) => ({ ...current, propertyId: id, propertyName: name }));
  }, []);

  const setDates = useCallback((checkIn: string, checkOut: string) => {
    setState((current) => ({ ...current, checkIn, checkOut }));
  }, []);

  const setOccupancy = useCallback((adults: number, children: number, rooms: number) => {
    setState((current) => ({ ...current, adults, children, rooms }));
  }, []);

  const setSelectedRooms = useCallback((rooms: BookingState['selectedRooms']) => {
    setState((current) => ({ ...current, selectedRooms: rooms, idempotencyKey: generateId() }));
  }, []);

  const setGuestDetails = useCallback((details: BookingState['guestDetails']) => {
    setState((current) => ({ ...current, guestDetails: details }));
  }, []);

  const setBookingReference = useCallback((ref: string) => {
    setState((current) => ({ ...current, bookingReference: ref }));
  }, []);

  const setGuestAuth = useCallback((token: string, guestId: string) => {
    setState((current) => ({ ...current, accessToken: token, guestId }));
  }, []);

  const setReservation = useCallback((id: string, ref: string, guestId: string) => {
    setState((current) => ({
      ...current,
      reservationId: id,
      bookingReference: ref,
      guestId,
    }));
  }, []);

  const setPayment = useCallback((paymentId: string) => {
    setState((current) => ({ ...current, paymentId }));
  }, []);

  const setGateway = useCallback((type: number, name: string) => {
    setState((current) => ({ ...current, gatewayType: type, gatewayName: name }));
  }, []);

  const setGrandTotal = useCallback((total: string) => {
    setState((current) => ({ ...current, grandTotal: total }));
  }, []);

  const setCurrency = useCallback((currency: string) => {
    setState((current) => ({ ...current, currency }));
  }, []);

  const setIdempotencyKey = useCallback((key: string) => {
    setState((current) => ({ ...current, idempotencyKey: key }));
  }, []);

  const reset = useCallback(() => {
    try {
      sessionStorage.removeItem(BOOKING_STORAGE_KEY);
    } catch {
      // storage may be disabled
    }
    setState(DEFAULT_STATE);
  }, []);

  const value = useMemo<BookingContextValue>(
    () => ({
      state,
      setProperty,
      setDates,
      setOccupancy,
      setSelectedRooms,
      setGuestDetails,
      setBookingReference,
      setGuestAuth,
      setReservation,
      setPayment,
      setGateway,
      setGrandTotal,
      setCurrency,
      setIdempotencyKey,
      reset,
    }),
    [
      state,
      setProperty,
      setDates,
      setOccupancy,
      setSelectedRooms,
      setGuestDetails,
      setBookingReference,
      setGuestAuth,
      setReservation,
      setPayment,
      setGateway,
      setGrandTotal,
      setCurrency,
      setIdempotencyKey,
      reset,
    ],
  );

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
}

export function useBooking(): BookingContextValue {
  const ctx = useContext(BookingContext);
  if (ctx === null) {
    throw new Error('useBooking must be used inside a <BookingProvider>');
  }
  return ctx;
}
